import React, { useState, useRef } from 'react';
import { Info, Plus, Check, X, AlertCircle, Sparkles, GripVertical, Edit, Trash2, Camera, Lightbulb, Home, MapPin, Maximize, Layers, DoorOpen, Bath, Upload, Link2 } from 'lucide-react';
import { MobileLayout } from '../components/MobileLayout';
import { Tabs } from '../components/ui/Tabs';
import { Card, CardContent } from '../components/ui/Card';
// Types
interface DesiderataItem {
  id: string;
  text: string;
  active: boolean;
}
interface PhotoItem {
  id: string;
  url: string;
  room: string;
}
export function SchedaProprietaPage() {
  // State: General Info
  const [generalInfo, setGeneralInfo] = useState({
    address: 'Via dei Giardini 12',
    city: 'Milano',
    floor: '3',
    sqm: '120',
    rooms: '4',
    bathrooms: '2'
  });
  // State: Materials
  const [materials, setMaterials] = useState<string[]>(['Parquet antico', 'Marmo Carrara', 'Tessuti delicati']);
  const [newMaterial, setNewMaterial] = useState('');
  const [materialNote, setMaterialNote] = useState('Il parquet in salotto è molto delicato, usare solo prodotti specifici e panno ben strizzato.');
  // State: Products
  const [favProducts, setFavProducts] = useState<string[]>(['Detergente Bio', 'Anticalcare X', 'Sgrassatore Limone']);
  const [avoidProducts, setAvoidProducts] = useState<string[]>(['Candeggina', 'Ammoniaca']);
  const [newFavProduct, setNewFavProduct] = useState('');
  const [newAvoidProduct, setNewAvoidProduct] = useState('');
  // State: Access
  const [accessInfo, setAccessInfo] = useState({
    type: 'codice',
    code: '1234 #',
    notes: 'Il tastierino si trova sulla sinistra del portone.'
  });
  // Tab State
  const [activeTab, setActiveTab] = useState('informazioni');
  // State: Storico Filter
  const [storicoFilter, setStoricoFilter] = useState<'tutti' | 'pulizie' | 'straordinari'>('tutti');
  // Mock data: Storico interventions
  const storicoData = [{
    id: '1',
    type: 'pulizia',
    icon: '🧹',
    title: 'Pulizia ordinaria',
    date: '15 Gen 2024',
    duration: '3h 45min',
    status: 'Completato'
  }, {
    id: '2',
    type: 'straordinario',
    icon: '⚡',
    title: 'Lampadina bagno',
    date: '15 Gen 2024',
    duration: '30min',
    status: 'Completato'
  }, {
    id: '3',
    type: 'pulizia',
    icon: '🧹',
    title: 'Deep cleaning',
    date: '10 Gen 2024',
    duration: '5h',
    status: 'Completato'
  }, {
    id: '4',
    type: 'straordinario',
    icon: '⚡',
    title: 'Riparazione rubinetto',
    date: '8 Gen 2024',
    duration: '1h 15min',
    status: 'Completato'
  }, {
    id: '5',
    type: 'pulizia',
    icon: '🧹',
    title: 'Pulizia ordinaria',
    date: '5 Gen 2024',
    duration: '3h 30min',
    status: 'Completato'
  }];
  // Filter storico data based on selected filter
  const filteredStorico = storicoData.filter(item => {
    if (storicoFilter === 'tutti') return true;
    if (storicoFilter === 'pulizie') return item.type === 'pulizia';
    if (storicoFilter === 'straordinari') return item.type === 'straordinario';
    return true;
  });
  // State: Desiderata
  const [desiderata, setDesiderata] = useState<DesiderataItem[]>([{
    id: '1',
    text: 'Lenzuola sempre stirate',
    active: true
  }, {
    id: '2',
    text: 'Cuscini divano sistemati in modo specifico',
    active: true
  }, {
    id: '3',
    text: 'Asciugamani piegati a cigno',
    active: false
  }, {
    id: '4',
    text: 'Lasciare luci ingresso accese',
    active: true
  }]);
  const [newDesiderata, setNewDesiderata] = useState('');
  // State: Photos
  const [photos, setPhotos] = useState<PhotoItem[]>([{
    id: '1',
    url: 'https://cdn.magicpatterns.com/uploads/dVhw2cGTmUhE4Ap2uCd1sm/_livingroom_clean.png',
    room: 'Soggiorno'
  }, {
    id: '2',
    url: 'https://cdn.magicpatterns.com/uploads/q7HEF8xL4ZfPHBQejbrvyX/_kitchen_clean.png',
    room: 'Cucina'
  }, {
    id: '3',
    url: 'https://cdn.magicpatterns.com/uploads/9cNyWn1us7ZSVXzYMKkr7V/_bathroom_clean.png',
    room: 'Bagno'
  }, {
    id: '4',
    url: 'https://cdn.magicpatterns.com/uploads/vL5vGhBPD2XELKb3m3514D/_bedroom_clean.png',
    room: 'Camera'
  }]);
  // State: Add Photo Modal
  const [photoModalRoom, setPhotoModalRoom] = useState<string | null>(null);
  const [newPhotoUrl, setNewPhotoUrl] = useState('');
  const [photoSourceTab, setPhotoSourceTab] = useState<'upload' | 'url'>('upload');
  const [isDraggingPhoto, setIsDraggingPhoto] = useState(false);
  const photoFileInputRef = useRef<HTMLInputElement>(null);
  // State: Add New Room Modal
  const [newRoomModalOpen, setNewRoomModalOpen] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomPhotoUrl, setNewRoomPhotoUrl] = useState('');
  const [roomSourceTab, setRoomSourceTab] = useState<'upload' | 'url'>('upload');
  const [isDraggingRoom, setIsDraggingRoom] = useState(false);
  const roomFileInputRef = useRef<HTMLInputElement>(null);
  // File handling helpers
  const readFileAsDataUrl = (file: File, onLoad: (dataUrl: string) => void) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = e => {
      const result = e.target?.result;
      if (typeof result === 'string') onLoad(result);
    };
    reader.readAsDataURL(file);
  };
  // Preset photo suggestions for both modals
  const presetPhotos = ['https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&q=80&w=400', 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?auto=format&fit=crop&q=80&w=400', 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&q=80&w=400', 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&q=80&w=400'];
  const closePhotoModal = () => {
    setPhotoModalRoom(null);
    setNewPhotoUrl('');
    setPhotoSourceTab('upload');
    setIsDraggingPhoto(false);
  };
  const handleAddPhoto = () => {
    if (!photoModalRoom || !newPhotoUrl.trim()) return;
    setPhotos([...photos, {
      id: Date.now().toString(),
      url: newPhotoUrl.trim(),
      room: photoModalRoom
    }]);
    closePhotoModal();
  };
  const closeRoomModal = () => {
    setNewRoomModalOpen(false);
    setNewRoomName('');
    setNewRoomPhotoUrl('');
    setRoomSourceTab('upload');
    setIsDraggingRoom(false);
  };
  const handleAddRoom = () => {
    if (!newRoomName.trim()) return;
    const roomName = newRoomName.trim();
    const photoUrl = newRoomPhotoUrl.trim() || presetPhotos[0];
    setPhotos([...photos, {
      id: Date.now().toString(),
      url: photoUrl,
      room: roomName
    }]);
    closeRoomModal();
  };
  // Handlers
  const handleAddMaterial = () => {
    if (newMaterial.trim()) {
      setMaterials([...materials, newMaterial.trim()]);
      setNewMaterial('');
    }
  };
  const removeMaterial = (index: number) => {
    setMaterials(materials.filter((_, i) => i !== index));
  };
  const handleAddFavProduct = () => {
    if (newFavProduct.trim()) {
      setFavProducts([...favProducts, newFavProduct.trim()]);
      setNewFavProduct('');
    }
  };
  const handleAddAvoidProduct = () => {
    if (newAvoidProduct.trim()) {
      setAvoidProducts([...avoidProducts, newAvoidProduct.trim()]);
      setNewAvoidProduct('');
    }
  };
  const toggleDesiderata = (id: string) => {
    setDesiderata(desiderata.map(item => item.id === id ? {
      ...item,
      active: !item.active
    } : item));
  };
  const deleteDesiderata = (id: string) => {
    setDesiderata(desiderata.filter(item => item.id !== id));
  };
  const addDesiderata = () => {
    if (newDesiderata.trim()) {
      setDesiderata([...desiderata, {
        id: Date.now().toString(),
        text: newDesiderata.trim(),
        active: true
      }]);
      setNewDesiderata('');
    }
  };
  const addAISuggestion = () => {
    setDesiderata([...desiderata, {
      id: Date.now().toString(),
      text: 'Attenzione al parquet zona ingresso',
      active: true
    }]);
  };
  return <MobileLayout data-id="element-3716">
      {/* Page Header Row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 lg:mb-8" data-id="element-3717">
        <div data-id="element-3718">
          <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-3719">
            Scheda Proprietà
          </h1>
        </div>
        <button className="w-full sm:w-auto px-4 py-2.5 bg-coral text-white rounded-xl hover:bg-coral-600 transition-colors text-sm min-h-[44px]" data-id="element-3720">
          Salva Modifiche
        </button>
      </div>

      {/* Tab Navigation - Pill style (same as Pulizie) */}
      <Tabs className="mb-6 -mx-4 px-4 lg:mx-0 lg:px-0" scrollable value={activeTab} onValueChange={setActiveTab} items={[{
      id: 'informazioni',
      label: 'Informazioni'
    }, {
      id: 'accesso',
      label: 'Accesso'
    }, {
      id: 'preferenze',
      label: 'Preferenze'
    }, {
      id: 'attenzione',
      label: 'Punti di Attenzione'
    }]} data-id="element-3721" />

      {/* Info Generali Card - Tab: Informazioni */}
      {activeTab === 'informazioni' && <>
          <Card className="shadow-sm mb-4 lg:mb-6" data-id="element-3722">
            <CardContent noPadding className="p-4 lg:p-6" data-id="element-3723">
              <div className="mb-4 lg:mb-6" data-id="element-3724">
                <h2 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-3725">
                  <Home size={18} data-id="element-3726" />
                  Info Generali
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-id="element-3727">
                <div data-id="element-3728">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-3729">
                    Indirizzo
                  </label>
                  <div className="relative" data-id="element-3730">
                    <MapPin className="absolute left-3 top-2.5 text-gray-400" size={16} data-id="element-3731" />
                    <input type="text" value={generalInfo.address} onChange={e => setGeneralInfo({
                  ...generalInfo,
                  address: e.target.value
                })} className="w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-3732" />
                  </div>
                </div>
                <div data-id="element-3733">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-3734">
                    Città
                  </label>
                  <input type="text" value={generalInfo.city} onChange={e => setGeneralInfo({
                ...generalInfo,
                city: e.target.value
              })} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-3735" />
                </div>
                <div data-id="element-3736">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-3737">
                    Piano
                  </label>
                  <div className="relative" data-id="element-3738">
                    <Layers className="absolute left-3 top-2.5 text-gray-400" size={16} data-id="element-3739" />
                    <input type="text" value={generalInfo.floor} onChange={e => setGeneralInfo({
                  ...generalInfo,
                  floor: e.target.value
                })} className="w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-3740" />
                  </div>
                </div>
                <div data-id="element-3741">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-3742">
                    Mq
                  </label>
                  <div className="relative" data-id="element-3743">
                    <Maximize className="absolute left-3 top-2.5 text-gray-400" size={16} data-id="element-3744" />
                    <input type="text" value={generalInfo.sqm} onChange={e => setGeneralInfo({
                  ...generalInfo,
                  sqm: e.target.value
                })} className="w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-3745" />
                  </div>
                </div>
                <div data-id="element-3746">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-3747">
                    Numero Stanze
                  </label>
                  <div className="relative" data-id="element-3748">
                    <DoorOpen className="absolute left-3 top-2.5 text-gray-400" size={16} data-id="element-3749" />
                    <input type="text" value={generalInfo.rooms} onChange={e => setGeneralInfo({
                  ...generalInfo,
                  rooms: e.target.value
                })} className="w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-3750" />
                  </div>
                </div>
                <div data-id="element-3751">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-3752">
                    Numero Bagni
                  </label>
                  <div className="relative" data-id="element-3753">
                    <Bath className="absolute left-3 top-2.5 text-gray-400" size={16} data-id="element-3754" />
                    <input type="text" value={generalInfo.bathrooms} onChange={e => setGeneralInfo({
                  ...generalInfo,
                  bathrooms: e.target.value
                })} className="w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all min-h-[44px]" data-id="element-3755" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Foto Riferimento Card */}
          <Card className="shadow-sm" data-id="element-3756">
            <CardContent noPadding className="p-4 lg:p-6" data-id="element-3757">
              <div className="mb-4 lg:mb-6" data-id="element-3758">
                <h2 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-3759">
                  <Camera size={18} data-id="element-3760" />
                  Foto Riferimento
                </h2>
                <p className="text-xs lg:text-sm text-gray-500 mt-1" data-id="element-3761">
                  Queste foto verranno usate dall'AI per confrontare il
                  prima/dopo
                </p>
              </div>

              <div className="space-y-4 lg:space-y-6" data-id="element-3762">
                {Array.from(new Set(photos.map(p => p.room))).map(roomName => {
              const roomPhotos = photos.filter(p => p.room === roomName);
              return <div key={roomName} className="space-y-3" data-id="element-3763">
                        <div className="flex items-center justify-between" data-id="element-3764">
                          <h3 className="text-sm font-semibold text-navy flex items-center gap-2" data-id="element-3765">
                            <DoorOpen size={16} className="text-teal-deep" data-id="element-3766" />
                            {roomName}
                          </h3>
                          <span className="text-xs text-gray-500" data-id="element-3767">
                            {roomPhotos.length} foto
                          </span>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-3 p-3 lg:p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-3768">
                          {roomPhotos.map(photo => <div key={photo.id} className="group relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-100" data-id="element-3769">
                              <img src={photo.url} alt={photo.room} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-3770" />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-start justify-end p-2" data-id="element-3771">
                                <div className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex gap-1" data-id="element-3772">
                                  <button className="p-1.5 bg-white text-gray-700 rounded-full hover:text-navy shadow-sm" data-id="element-3773">
                                    <Edit size={12} data-id="element-3774" />
                                  </button>
                                  <button onClick={() => setPhotos(photos.filter(p => p.id !== photo.id))} className="p-1.5 bg-white text-red-500 rounded-full hover:text-red-700 shadow-sm" data-id="element-3775">
                                    <Trash2 size={12} data-id="element-3776" />
                                  </button>
                                </div>
                              </div>
                            </div>)}

                          <button onClick={() => setPhotoModalRoom(roomName)} className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-teal-deep hover:bg-white transition-all flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-teal-deep min-h-[44px]" data-id="element-3777">
                            <Plus size={20} data-id="element-3778" />
                            <span className="text-xs font-medium" data-id="element-3779">
                              Aggiungi
                            </span>
                          </button>
                        </div>
                      </div>;
            })}

                <button onClick={() => setNewRoomModalOpen(true)} className="w-full p-3 lg:p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-teal-deep hover:bg-mint-light transition-all flex items-center justify-center gap-2 text-gray-500 hover:text-teal-deep min-h-[44px]" data-id="element-3780">
                  <Plus size={18} data-id="element-3781" />
                  <span className="text-sm font-medium" data-id="element-3782">
                    Aggiungi nuova stanza
                  </span>
                </button>
              </div>
            </CardContent>
          </Card>
        </>}

      {/* Punti di Attenzione Tab */}
      {activeTab === 'attenzione' && <Card className="shadow-sm" data-id="element-3783">
          <CardContent noPadding className="p-4 lg:p-6" data-id="element-3784">
            <div className="mb-4 lg:mb-6" data-id="element-3785">
              <h2 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-3786">
                <Info size={18} data-id="element-3787" />
                Materiali & Attenzioni
              </h2>
              <p className="text-xs lg:text-sm text-gray-500 mt-1" data-id="element-3788">
                Indica i materiali presenti che richiedono attenzione
                particolare
              </p>
            </div>

            <div className="space-y-4" data-id="element-3789">
              <div className="flex flex-wrap gap-2" data-id="element-3790">
                {materials.map((material, index) => <span key={index} className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200" data-id="element-3791">
                    {material}
                    <button onClick={() => removeMaterial(index)} className="ml-2 text-gray-400 hover:text-gray-600" data-id="element-3792">
                      <X size={14} data-id="element-3793" />
                    </button>
                  </span>)}
              </div>

              <div className="flex gap-2" data-id="element-3794">
                <input type="text" value={newMaterial} onChange={e => setNewMaterial(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddMaterial()} placeholder="Aggiungi materiale..." className="flex-1 px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none min-h-[44px]" data-id="element-3795" />
                <button onClick={handleAddMaterial} disabled={!newMaterial.trim()} className="px-3 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-3796">
                  <Plus size={18} data-id="element-3797" />
                </button>
              </div>

              <div data-id="element-3798">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-3799">
                  Note aggiuntive sui materiali
                </label>
                <textarea value={materialNote} onChange={e => setMaterialNote(e.target.value)} rows={3} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none transition-all resize-none" placeholder="Inserisci dettagli specifici su come trattare le superfici..." data-id="element-3800" />
              </div>
            </div>
          </CardContent>
        </Card>}

      {/* Preferenze Pulizia Tab */}
      {activeTab === 'preferenze' && <>
          <Card className="shadow-sm mb-4 lg:mb-6" data-id="element-3801">
            <CardContent noPadding className="p-4 lg:p-6" data-id="element-3802">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" data-id="element-3803">
                {/* Preferred Products */}
                <div data-id="element-3804">
                  <div className="mb-4" data-id="element-3805">
                    <h3 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-3806">
                      <div className="p-1 bg-green-100 rounded-full text-green-600" data-id="element-3807">
                        <Check size={14} data-id="element-3808" />
                      </div>
                      Prodotti Preferiti
                    </h3>
                  </div>

                  <div data-id="element-3809">
                    <div className="flex flex-wrap gap-2 mb-3" data-id="element-3810">
                      {favProducts.map((product, index) => <span key={index} className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-3811">
                          {product}
                          <button onClick={() => setFavProducts(favProducts.filter((_, i) => i !== index))} className="ml-2 text-green-500 hover:text-green-700" data-id="element-3812">
                            <X size={14} data-id="element-3813" />
                          </button>
                        </span>)}
                    </div>
                    <div className="flex gap-2" data-id="element-3814">
                      <input type="text" value={newFavProduct} onChange={e => setNewFavProduct(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddFavProduct()} placeholder="Nuovo prodotto..." className="flex-1 px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none min-h-[44px]" data-id="element-3815" />
                      <button onClick={handleAddFavProduct} disabled={!newFavProduct.trim()} className="px-3 py-2.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 disabled:opacity-50 min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-3816">
                        <Plus size={18} data-id="element-3817" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Avoided Products */}
                <div data-id="element-3818">
                  <div className="mb-4" data-id="element-3819">
                    <h3 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-3820">
                      <div className="p-1 bg-red-100 rounded-full text-red-600" data-id="element-3821">
                        <X size={14} data-id="element-3822" />
                      </div>
                      Prodotti da Evitare
                    </h3>
                  </div>

                  <div data-id="element-3823">
                    <div className="flex flex-wrap gap-2 mb-3" data-id="element-3824">
                      {avoidProducts.map((product, index) => <span key={index} className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-100" data-id="element-3825">
                          {product}
                          <button onClick={() => setAvoidProducts(avoidProducts.filter((_, i) => i !== index))} className="ml-2 text-red-500 hover:text-red-700" data-id="element-3826">
                            <X size={14} data-id="element-3827" />
                          </button>
                        </span>)}
                    </div>
                    <div className="flex gap-2" data-id="element-3828">
                      <input type="text" value={newAvoidProduct} onChange={e => setNewAvoidProduct(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddAvoidProduct()} placeholder="Prodotto da evitare..." className="flex-1 px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none min-h-[44px]" data-id="element-3829" />
                      <button onClick={handleAddAvoidProduct} disabled={!newAvoidProduct.trim()} className="px-3 py-2.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 disabled:opacity-50 min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-3830">
                        <Plus size={18} data-id="element-3831" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Desiderata Card */}
          <Card className="shadow-sm" data-id="element-3832">
            <CardContent noPadding className="p-4 lg:p-6" data-id="element-3833">
              <div className="mb-4 lg:mb-6" data-id="element-3834">
                <h2 className="text-sm font-medium text-navy flex items-center gap-2" data-id="element-3835">
                  <Sparkles size={18} className="text-coral" data-id="element-3836" />
                  Desiderata Pulizia
                </h2>
                <p className="text-xs lg:text-sm text-gray-500 mt-1" data-id="element-3837">
                  Questi elementi verranno aggiunti automaticamente alla
                  checklist del fornitore
                </p>
              </div>

              <div className="space-y-3 mb-6" data-id="element-3838">
                {desiderata.map(item => <div key={item.id} className="group flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-300 transition-all" data-id="element-3839">
                    <div className="cursor-grab text-gray-400 hover:text-gray-600 hidden sm:block" data-id="element-3840">
                      <GripVertical size={18} data-id="element-3841" />
                    </div>
                    <button onClick={() => toggleDesiderata(item.id)} className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-deep focus:ring-offset-2 flex-shrink-0 ${item.active ? 'bg-teal-deep' : 'bg-gray-300'}`} data-id="element-3842">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.active ? 'translate-x-5' : 'translate-x-0.5'}`} data-id="element-3843" />
                    </button>
                    <span className={`flex-1 text-sm ${item.active ? 'text-gray-700' : 'text-gray-400 line-through'}`} data-id="element-3844">
                      {item.text}
                    </span>
                    <div className="flex items-center gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" data-id="element-3845">
                      <button className="p-1.5 text-gray-400 hover:text-navy hover:bg-white rounded-md" data-id="element-3846">
                        <Edit size={14} data-id="element-3847" />
                      </button>
                      <button onClick={() => deleteDesiderata(item.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-white rounded-md" data-id="element-3848">
                        <Trash2 size={14} data-id="element-3849" />
                      </button>
                    </div>
                  </div>)}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mb-6" data-id="element-3850">
                <input type="text" value={newDesiderata} onChange={e => setNewDesiderata(e.target.value)} onKeyDown={e => e.key === 'Enter' && addDesiderata()} placeholder="Aggiungi una nuova richiesta specifica..." className="flex-1 px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none min-h-[44px]" data-id="element-3851" />
                <button onClick={addDesiderata} disabled={!newDesiderata.trim()} className="px-4 py-2.5 bg-coral text-white rounded-xl hover:bg-coral-600 disabled:opacity-50 font-medium text-sm min-h-[44px] whitespace-nowrap" data-id="element-3852">
                  Aggiungi
                </button>
              </div>

              <div className="pt-6 border-t border-gray-100" data-id="element-3853">
                <div className="bg-coral/5 border border-coral/20 rounded-lg p-4 flex items-start gap-3" data-id="element-3854">
                  <div className="p-2 bg-white rounded-full text-yellow-500 shadow-sm flex-shrink-0" data-id="element-3855">
                    <Lightbulb size={18} data-id="element-3856" />
                  </div>
                  <div className="flex-1" data-id="element-3857">
                    <p className="text-sm text-coral font-medium mb-1" data-id="element-3858">
                      Suggerimento AI
                    </p>
                    <p className="text-xs text-coral mb-3" data-id="element-3859">
                      In base alle segnalazioni precedenti, considera di
                      aggiungere "Attenzione al parquet zona ingresso"
                    </p>
                    <button onClick={addAISuggestion} className="text-xs font-medium text-coral hover:text-coral-600 underline" data-id="element-3860">
                      Aggiungi ai desiderata
                    </button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>}

      {/* Accesso Tab */}
      {activeTab === 'accesso' && <Card className="shadow-sm" data-id="element-3861">
          <CardContent noPadding className="p-4 lg:p-6" data-id="element-3862">
            <div className="mb-4 lg:mb-6" data-id="element-3863">
              <h2 className="text-sm font-medium text-navy" data-id="element-3864">
                Istruzioni Accesso
              </h2>
            </div>

            <div className="bg-coral/5 border-l-4 border-coral p-4 lg:p-6 rounded-r-lg mb-6" data-id="element-3865">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6" data-id="element-3866">
                <div data-id="element-3867">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-3868">
                    Tipo Accesso
                  </label>
                  <select value={accessInfo.type} onChange={e => setAccessInfo({
                ...accessInfo,
                type: e.target.value
              })} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-3869">
                    <option value="chiavi" data-id="element-3870">Chiavi fisiche</option>
                    <option value="codice" data-id="element-3871">Codice tastierino</option>
                    <option value="portineria" data-id="element-3872">Portineria</option>
                    <option value="smart" data-id="element-3873">Smart Lock</option>
                    <option value="vickey" data-id="element-3874">Vickey</option>
                  </select>
                </div>
                <div data-id="element-3875">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-3876">
                    Codice / Info Chiavi
                  </label>
                  <input type="text" value={accessInfo.code} onChange={e => setAccessInfo({
                ...accessInfo,
                code: e.target.value
              })} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none min-h-[44px]" data-id="element-3877" />
                </div>
                <div className="sm:col-span-2" data-id="element-3878">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-3879">
                    Note Accesso
                  </label>
                  <textarea value={accessInfo.notes} onChange={e => setAccessInfo({
                ...accessInfo,
                notes: e.target.value
              })} rows={2} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none resize-none" data-id="element-3880" />
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100" data-id="element-3881">
              <div className="flex items-start gap-2 text-xs text-coral font-medium" data-id="element-3882">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" data-id="element-3883" />
                <span data-id="element-3884">
                  Queste informazioni saranno visibili al fornitore solo dopo
                  conferma intervento
                </span>
              </div>
            </div>
          </CardContent>
        </Card>}

      {/* Storico Tab */}
      {activeTab === 'storico' && <div className="bg-white rounded-xl border border-border shadow-sm p-4 lg:p-6" data-id="element-3885">
          <div className="mb-4 lg:mb-6" data-id="element-3886">
            <h2 className="text-sm font-medium text-navy" data-id="element-3887">
              Storico Interventi
            </h2>
            <p className="text-xs lg:text-sm text-gray-500 mt-1" data-id="element-3888">
              Cronologia di tutti gli interventi effettuati
            </p>
          </div>

          {/* Filter Buttons */}
          <div className="flex gap-2 mb-4" data-id="element-3889">
            <button onClick={() => setStoricoFilter('tutti')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${storicoFilter === 'tutti' ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3890">
              Tutti
            </button>
            <button onClick={() => setStoricoFilter('pulizie')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${storicoFilter === 'pulizie' ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3891">
              Pulizie
            </button>
            <button onClick={() => setStoricoFilter('straordinari')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${storicoFilter === 'straordinari' ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3892">
              Straordinari
            </button>
          </div>

          {/* History List */}
          <div className="space-y-3" data-id="element-3893">
            {filteredStorico.map(item => <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="element-3894">
                <div className="flex items-center gap-3" data-id="element-3895">
                  <span className="text-lg" data-id="element-3896">{item.icon}</span>
                  <div data-id="element-3897">
                    <div className="text-sm font-medium text-navy" data-id="element-3898">
                      {item.title}
                    </div>
                    <div className="text-xs text-gray-400" data-id="element-3899">{item.date}</div>
                  </div>
                </div>
                <div className="text-right" data-id="element-3900">
                  <div className="text-sm text-gray-600" data-id="element-3901">{item.duration}</div>
                  <div className="text-xs text-green-600" data-id="element-3902">✓ {item.status}</div>
                </div>
              </div>)}

            {filteredStorico.length === 0 && <div className="text-center py-8 text-gray-400 text-sm" data-id="element-3903">
                Nessun intervento trovato
              </div>}
          </div>
        </div>}

      {/* Add Photo Modal */}
      {photoModalRoom && <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={closePhotoModal} data-id="element-3904">
          <div className="bg-white rounded-2xl border border-border w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-3905">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border" data-id="element-3906">
              <div data-id="element-3907">
                <h2 className="text-base font-bold text-navy" data-id="element-3908">Aggiungi foto</h2>
                <p className="text-xs text-muted-foreground mt-0.5" data-id="element-3909">
                  Stanza:{' '}
                  <span className="font-medium text-foreground" data-id="element-3910">
                    {photoModalRoom}
                  </span>
                </p>
              </div>
              <button onClick={closePhotoModal} className="p-1.5 hover:bg-muted rounded-full transition-colors text-muted-foreground" aria-label="Chiudi" data-id="element-3911">
                <X size={18} data-id="element-3912" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4" data-id="element-3913">
              {/* Source tabs: Upload | URL */}
              <div className="flex gap-1 p-1 bg-muted rounded-lg" data-id="element-3914">
                <button onClick={() => setPhotoSourceTab('upload')} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${photoSourceTab === 'upload' ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-3915">
                  <Upload size={14} data-id="element-3916" />
                  Carica file
                </button>
                <button onClick={() => setPhotoSourceTab('url')} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${photoSourceTab === 'url' ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-3917">
                  <Link2 size={14} data-id="element-3918" />
                  URL
                </button>
              </div>

              {/* Upload mode */}
              {photoSourceTab === 'upload' && <div data-id="element-3919">
                  <input ref={photoFileInputRef} type="file" accept="image/*" className="hidden" onChange={e => {
              const file = e.target.files?.[0];
              if (file) readFileAsDataUrl(file, setNewPhotoUrl);
            }} data-id="element-3920" />
                  {!newPhotoUrl ? <div onClick={() => photoFileInputRef.current?.click()} onDragOver={e => {
              e.preventDefault();
              setIsDraggingPhoto(true);
            }} onDragLeave={() => setIsDraggingPhoto(false)} onDrop={e => {
              e.preventDefault();
              setIsDraggingPhoto(false);
              const file = e.dataTransfer.files?.[0];
              if (file) readFileAsDataUrl(file, setNewPhotoUrl);
            }} className={`flex flex-col items-center justify-center gap-3 px-4 py-10 rounded-lg border-2 border-dashed cursor-pointer transition-all ${isDraggingPhoto ? 'border-coral bg-coral/5' : 'border-border hover:border-foreground/30 hover:bg-muted/30'}`} data-id="element-3921">
                      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center" data-id="element-3922">
                        <Upload size={22} className={isDraggingPhoto ? 'text-coral' : 'text-muted-foreground'} data-id="element-3923" />
                      </div>
                      <div className="text-center" data-id="element-3924">
                        <p className="text-sm font-medium text-foreground" data-id="element-3925">
                          Trascina qui un'immagine
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5" data-id="element-3926">
                          oppure clicca per scegliere un file
                        </p>
                      </div>
                      <p className="text-[10px] text-muted-foreground" data-id="element-3927">
                        PNG, JPG, WEBP fino a 10MB
                      </p>
                    </div> : <div className="space-y-2" data-id="element-3928">
                      <div className="relative aspect-video rounded-lg overflow-hidden border border-border bg-gray-100" data-id="element-3929">
                        <img src={newPhotoUrl} alt="Anteprima" className="w-full h-full object-cover" data-id="element-3930" />
                        <button onClick={() => setNewPhotoUrl('')} className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-sm hover:bg-muted transition-colors" aria-label="Rimuovi" data-id="element-3931">
                          <X size={14} className="text-foreground" data-id="element-3932" />
                        </button>
                      </div>
                      <button onClick={() => photoFileInputRef.current?.click()} className="text-xs font-medium text-coral hover:underline" data-id="element-3933">
                        Scegli un'altra immagine
                      </button>
                    </div>}
                </div>}

              {/* URL mode */}
              {photoSourceTab === 'url' && <>
                  <div data-id="element-3934">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-3935">
                      URL Immagine
                    </label>
                    <input type="text" value={newPhotoUrl.startsWith('data:') ? '' : newPhotoUrl} onChange={e => setNewPhotoUrl(e.target.value)} placeholder="https://..." className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-3936" />
                  </div>

                  {newPhotoUrl.trim() && !newPhotoUrl.startsWith('data:') && <div data-id="element-3937">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-3938">
                        Anteprima
                      </label>
                      <div className="aspect-video rounded-lg overflow-hidden border border-border bg-gray-100" data-id="element-3939">
                        <img src={newPhotoUrl} alt="Anteprima" className="w-full h-full object-cover" onError={e => {
                  ;
                  (e.target as HTMLImageElement).style.display = 'none';
                }} data-id="element-3940" />
                      </div>
                    </div>}
                </>}

              {/* Gallery (always available) */}
              <div className="pt-2 border-t border-border" data-id="element-3941">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 mt-3" data-id="element-3942">
                  Oppure scegli da una galleria
                </label>
                <div className="grid grid-cols-4 gap-2" data-id="element-3943">
                  {presetPhotos.map(url => <button key={url} onClick={() => setNewPhotoUrl(url)} className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${newPhotoUrl === url ? 'border-coral ring-2 ring-coral/20' : 'border-border hover:border-foreground/30'}`} data-id="element-3944">
                      <img src={url} alt="" className="w-full h-full object-cover" data-id="element-3945" />
                    </button>)}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border" data-id="element-3946">
              <button onClick={closePhotoModal} className="px-5 py-2.5 bg-white border border-border text-foreground rounded-full text-sm font-medium hover:bg-muted transition-colors min-h-[44px]" data-id="element-3947">
                Annulla
              </button>
              <button onClick={handleAddPhoto} disabled={!newPhotoUrl.trim()} className="px-5 py-2.5 bg-coral text-white rounded-xl text-sm font-semibold hover:bg-coral-600 transition-colors shadow-sm min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3948">
                Aggiungi foto
              </button>
            </div>
          </div>
        </div>}

      {/* Add New Room Modal */}
      {newRoomModalOpen && <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={closeRoomModal} data-id="element-3949">
          <div className="bg-white rounded-2xl border border-border w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-3950">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border" data-id="element-3951">
              <div data-id="element-3952">
                <h2 className="text-base font-bold text-navy" data-id="element-3953">
                  Aggiungi nuova stanza
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5" data-id="element-3954">
                  Crea una nuova stanza per organizzare le foto
                </p>
              </div>
              <button onClick={closeRoomModal} className="p-1.5 hover:bg-muted rounded-full transition-colors text-muted-foreground" aria-label="Chiudi" data-id="element-3955">
                <X size={18} data-id="element-3956" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4" data-id="element-3957">
              <div data-id="element-3958">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-3959">
                  Nome stanza *
                </label>
                <div className="relative" data-id="element-3960">
                  <DoorOpen className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} data-id="element-3961" />
                  <input type="text" value={newRoomName} onChange={e => setNewRoomName(e.target.value)} placeholder="Es. Camera ospiti, Bagno principale..." autoFocus className="w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-3962" />
                </div>
              </div>

              <div data-id="element-3963">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-3964">
                  Prima foto (opzionale)
                </label>

                {/* Source tabs */}
                <div className="flex gap-1 p-1 bg-muted rounded-lg mb-3" data-id="element-3965">
                  <button onClick={() => setRoomSourceTab('upload')} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${roomSourceTab === 'upload' ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-3966">
                    <Upload size={14} data-id="element-3967" />
                    Carica file
                  </button>
                  <button onClick={() => setRoomSourceTab('url')} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${roomSourceTab === 'url' ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-3968">
                    <Link2 size={14} data-id="element-3969" />
                    URL
                  </button>
                </div>

                {/* Upload mode */}
                {roomSourceTab === 'upload' && <>
                    <input ref={roomFileInputRef} type="file" accept="image/*" className="hidden" onChange={e => {
                const file = e.target.files?.[0];
                if (file) readFileAsDataUrl(file, setNewRoomPhotoUrl);
              }} data-id="element-3970" />
                    {!newRoomPhotoUrl ? <div onClick={() => roomFileInputRef.current?.click()} onDragOver={e => {
                e.preventDefault();
                setIsDraggingRoom(true);
              }} onDragLeave={() => setIsDraggingRoom(false)} onDrop={e => {
                e.preventDefault();
                setIsDraggingRoom(false);
                const file = e.dataTransfer.files?.[0];
                if (file) readFileAsDataUrl(file, setNewRoomPhotoUrl);
              }} className={`flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-lg border-2 border-dashed cursor-pointer transition-all ${isDraggingRoom ? 'border-coral bg-coral/5' : 'border-border hover:border-foreground/30 hover:bg-muted/30'}`} data-id="element-3971">
                        <Upload size={20} className={isDraggingRoom ? 'text-coral' : 'text-muted-foreground'} data-id="element-3972" />
                        <p className="text-xs text-foreground text-center" data-id="element-3973">
                          <span className="font-medium" data-id="element-3974">Trascina</span> o{' '}
                          <span className="font-medium" data-id="element-3975">clicca</span> per
                          scegliere
                        </p>
                      </div> : <div className="relative aspect-video rounded-lg overflow-hidden border border-border bg-gray-100" data-id="element-3976">
                        <img src={newRoomPhotoUrl} alt="Anteprima" className="w-full h-full object-cover" data-id="element-3977" />
                        <button onClick={() => setNewRoomPhotoUrl('')} className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-sm hover:bg-muted transition-colors" aria-label="Rimuovi" data-id="element-3978">
                          <X size={14} className="text-foreground" data-id="element-3979" />
                        </button>
                      </div>}
                  </>}

                {/* URL mode */}
                {roomSourceTab === 'url' && <input type="text" value={newRoomPhotoUrl.startsWith('data:') ? '' : newRoomPhotoUrl} onChange={e => setNewRoomPhotoUrl(e.target.value)} placeholder="https://..." className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-3980" />}
              </div>

              <div data-id="element-3981">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-3982">
                  Galleria suggerimenti
                </label>
                <div className="grid grid-cols-4 gap-2" data-id="element-3983">
                  {presetPhotos.map(url => <button key={url} onClick={() => setNewRoomPhotoUrl(url)} className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${newRoomPhotoUrl === url ? 'border-coral ring-2 ring-coral/20' : 'border-border hover:border-foreground/30'}`} data-id="element-3984">
                      <img src={url} alt="" className="w-full h-full object-cover" data-id="element-3985" />
                    </button>)}
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-3 flex items-start gap-2" data-id="element-3986">
                <Info size={14} className="text-muted-foreground flex-shrink-0 mt-0.5" data-id="element-3987" />
                <p className="text-xs text-muted-foreground leading-relaxed" data-id="element-3988">
                  Se non scegli una foto, useremo un'immagine di default. Potrai
                  sostituirla in seguito.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border" data-id="element-3989">
              <button onClick={closeRoomModal} className="px-5 py-2.5 bg-white border border-border text-foreground rounded-full text-sm font-medium hover:bg-muted transition-colors min-h-[44px]" data-id="element-3990">
                Annulla
              </button>
              <button onClick={handleAddRoom} disabled={!newRoomName.trim()} className="px-5 py-2.5 bg-coral text-white rounded-xl text-sm font-semibold hover:bg-coral-600 transition-colors shadow-sm min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3991">
                Crea stanza
              </button>
            </div>
          </div>
        </div>}
    </MobileLayout>;
}